import { useEffect, useState, type CSSProperties } from 'react';
import { apiFetch, getDisplayName } from '@/lib/auth';

interface GroupOption { id: string; name: string }

/**
 * Welle 8d/M2 + Welle 9 — Experten-Link-Erzeugung.
 *
 * Erzeugt einen PERSÖNLICHEN Link (kind='agent', agentId = eigene User-ID) oder einen
 * GRUPPEN-Link (kind='group'): wer ihn anklickt, landet im aktuellen Gespräch des
 * Beraters bzw. wird an einen verfügbaren Berater der Gruppe vermittelt. Optional als
 * Termin mit Datum + Start-/Endzeit. Nicht an einen konkreten Call gebunden.
 */
export function ExpertLinkCard({ onCreated }: { onCreated?: () => void } = {}) {
  const ownId = typeof localStorage !== 'undefined' ? localStorage.getItem('bamoa_user_id') : null;
  const [linkType, setLinkType] = useState<'agent' | 'group'>('agent');
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [groupId, setGroupId] = useState('');
  const [expertName, setExpertName] = useState('');
  const [apptDate, setApptDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [e2ee, setE2ee] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'creating' | 'done' | 'error'>('idle');
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  // Gruppen des Mandanten laden (für den Gruppen-Link-Typ).
  useEffect(() => {
    let alive = true;
    apiFetch('/api/groups')
      .then((r) => (r.ok ? r.json() : []))
      .then((data: GroupOption[]) => { if (alive && Array.isArray(data)) setGroups(data); })
      .catch(() => { /* Gruppen optional */ });
    return () => { alive = false; };
  }, []);

  const create = async () => {
    if (!ownId) { setErr('Kein angemeldeter Berater gefunden.'); setPhase('error'); return; }
    if (linkType === 'group' && !groupId) { setErr('Bitte eine Gruppe auswählen.'); setPhase('error'); return; }
    // Termin (optional): Datum + Beginn/Ende → validFrom/validUntil. Ohne Datum: Dauerlink.
    let validFrom: string | undefined;
    let validUntil: string | undefined;
    if (apptDate) {
      validFrom = startTime ? `${apptDate}T${startTime}:00` : `${apptDate}T00:00:00`;
      validUntil = endTime ? `${apptDate}T${endTime}:00` : `${apptDate}T23:59:59`;
      if (startTime && endTime && startTime >= endTime) {
        setErr('Die Endzeit muss nach der Startzeit liegen.');
        setPhase('error');
        return;
      }
    }
    setPhase('creating');
    setErr('');
    try {
      const me = getDisplayName();
      const groupLabel = groups.find((g) => g.id === groupId)?.name;
      const payload =
        linkType === 'group'
          ? { kind: 'group', groupId, targetLabel: groupLabel ? `Gruppe ${groupLabel}` : 'Beratungsteam', e2ee }
          : { kind: 'agent', agentId: ownId, targetLabel: me ? `Gespräch mit ${me}` : 'Videoberatung' };
      const res = await apiFetch('/api/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          expectedName: expertName.trim() || undefined,
          validFrom,
          validUntil,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setErr(b?.message || 'Link konnte nicht erzeugt werden.');
        setPhase('error');
        return;
      }
      const data = await res.json();
      setUrl(data.url);
      setPhase('done');
      onCreated?.();
    } catch {
      setErr('Verbindung fehlgeschlagen.');
      setPhase('error');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* Clipboard nicht verfügbar — Link steht sichtbar zum manuellen Kopieren. */ }
  };

  const sendMail = () => {
    const subject = encodeURIComponent('Einladung zur Videoberatung · Bamoa Videoservice');
    const greeting = expertName.trim() ? `Guten Tag ${expertName.trim()},` : 'Guten Tag,';
    let when = '';
    if (apptDate) {
      const d = new Date(`${apptDate}T${startTime || '00:00'}:00`);
      const dayStr = d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      when = startTime && endTime
        ? `\n\nTermin: ${dayStr}, ${startTime}–${endTime} Uhr.`
        : `\n\nTermin: ${dayStr}.`;
    }
    const body = encodeURIComponent(
      `${greeting}\n\nüber den folgenden Link können Sie sich meiner Videoberatung als Experte zuschalten:\n\n${url}${when}\n\n` +
        `Sie werden nach Ihrem Namen gefragt; den Beitritt bestätige ich anschließend. ` +
        `Eine Teilnahme ist auch per Smartphone oder Tablet im Browser möglich – ohne App.\n\nMit freundlichen Grüßen`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const reset = () => { setPhase('idle'); setUrl(''); setErr(''); setCopied(false); };

  return (
    <div className="now-card" style={cardStyle}>
      <div className="nc-eyebrow">Experten einladen</div>
      <div className="nc-title">Experten-Link erstellen</div>
      <div className="nc-sub" style={{ marginBottom: 14 }}>
        {linkType === 'group'
          ? <>Gruppen-Link. Wer ihn anklickt, ruft direkt in der gewählten Gruppe an – wie eine Stele. Ein verfügbarer Berater nimmt den Anruf an.</>
          : <>Persönlicher Einladungslink. Wer ihn anklickt, wird deinem <strong>aktuellen</strong> Gespräch zugeschaltet – du bestätigst den Beitritt.</>}
      </div>

      {phase !== 'done' && (
        <>
          {/* Link-Typ: persönlich vs. Gruppe */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, background: 'var(--bg-elev-2)', padding: 4, borderRadius: 10 }}>
            <button onClick={() => setLinkType('agent')} style={segStyle(linkType === 'agent')}>Persönlich</button>
            <button onClick={() => setLinkType('group')} style={segStyle(linkType === 'group')} disabled={groups.length === 0} title={groups.length === 0 ? 'Keine Gruppen verfügbar' : undefined}>Gruppe</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            {linkType === 'group' && (
              <label style={labelStyle}>
                <span style={labelText}>Gruppe</span>
                <select value={groupId} onChange={(e) => setGroupId(e.target.value)} style={inputStyle}>
                  <option value="">Bitte wählen…</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </label>
            )}
            {linkType === 'group' && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '2px 0' }}>
                <input
                  type="checkbox"
                  checked={e2ee}
                  onChange={(e) => setE2ee(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
                />
                <span style={{ ...labelText, marginBottom: 0 }}>
                  Ende-zu-Ende verschlüsseln (SFrame)
                  <span style={{ display: 'block', opacity: 0.55, fontWeight: 400, marginTop: 2 }}>
                    Nur aktivieren, wenn der Gast einen unterstützten Browser nutzt (Chrome/Edge am
                    Desktop). Auf iPhone/Safari nicht verfügbar — dann bleibt es transportverschlüsselt.
                  </span>
                </span>
              </label>
            )}
            <label style={labelStyle}>
              <span style={labelText}>Erwarteter Name <span style={{ opacity: 0.5 }}>(optional)</span></span>
              <input
                value={expertName}
                onChange={(e) => setExpertName(e.target.value)}
                placeholder="z. B. Dr. Berger, Steuerexperte"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span style={labelText}>Termin <span style={{ opacity: 0.5 }}>(optional)</span></span>
              <input
                type="date"
                value={apptDate}
                onChange={(e) => setApptDate(e.target.value)}
                style={inputStyle}
              />
            </label>
            {apptDate && (
              <div style={{ display: 'flex', gap: 10 }}>
                <label style={{ ...labelStyle, flex: 1 }}>
                  <span style={labelText}>Beginn</span>
                  <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={inputStyle} />
                </label>
                <label style={{ ...labelStyle, flex: 1 }}>
                  <span style={labelText}>Ende</span>
                  <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={inputStyle} />
                </label>
              </div>
            )}
          </div>
          <button className="drawer-cta" disabled={phase === 'creating' || !ownId} onClick={create} style={ctaStyle}>
            {phase === 'creating' ? 'Wird erzeugt…' : linkType === 'group' ? 'Gruppen-Link erzeugen' : 'Persönlichen Link erzeugen'}
          </button>
        </>
      )}

      {phase === 'error' && <div style={{ color: '#d8785a', fontSize: 13, marginTop: 10 }}>{err}</div>}

      {phase === 'done' && (
        <div>
          <div style={{ fontSize: 12.5, color: 'var(--muted, #8aa)', marginBottom: 6 }}>Dein Einladungslink:</div>
          <div style={linkBoxStyle}>{url}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="drawer-cta" onClick={copy} style={{ ...ctaStyle, flex: 1, minWidth: 120, marginTop: 0, background: 'var(--bg-elev-3)', color: 'inherit' }}>
              {copied ? '✓ Kopiert' : 'Kopieren'}
            </button>
            <button className="drawer-cta" onClick={sendMail} style={{ ...ctaStyle, flex: 1, minWidth: 120, marginTop: 0 }}>
              Per E-Mail senden
            </button>
          </div>
          <button onClick={reset} style={resetStyle}>Neuen Link erzeugen</button>
        </div>
      )}
    </div>
  );
}

const cardStyle: CSSProperties = { display: 'block' };
const labelStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5 };
const labelText: CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--muted, #8aa)', letterSpacing: '.02em' };
const inputStyle: CSSProperties = {
  width: '100%', padding: '9px 11px', borderRadius: 8,
  border: '1px solid var(--hairline)', background: 'var(--bg-elev-2)',
  color: 'inherit', fontSize: 14, fontFamily: 'inherit', outline: 'none',
};
const ctaStyle: CSSProperties = {
  width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none',
  background: 'var(--tenant-accent, #7c3aed)', color: '#fff', fontSize: 14, fontWeight: 600,
  cursor: 'pointer', marginTop: 4,
};
const linkBoxStyle: CSSProperties = {
  padding: '10px 12px', borderRadius: 8, background: 'var(--bg-elev-2)',
  border: '1px solid var(--hairline)', fontSize: 12.5, wordBreak: 'break-all',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.5,
};
const resetStyle: CSSProperties = {
  marginTop: 12, background: 'transparent', border: 'none', color: 'var(--muted, #8aa)',
  fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0,
};
function segStyle(active: boolean): CSSProperties {
  return {
    flex: 1, padding: '7px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
    background: active ? 'var(--tenant-accent, #7c3aed)' : 'transparent',
    color: active ? '#fff' : 'var(--muted, #8aa)',
  };
}
